import { useEffect, useRef } from "react";
import type { CSSProperties, ReactNode } from "react";

import TalentCard from "components/Screens/Marketplace/Customer/TalentNetwork/TalentCard";
import ProtoAvatar from "components/Screens/Marketplace/Customer/common/ProtoAvatar/ProtoAvatar";
import { ShortlistPreviewProvider, useShortlist } from "components/Screens/Marketplace/Customer/Shortlist/ShortlistContext";
import type { TalentListItem } from "components/Screens/Marketplace/Customer/types";
import { nextWeekdays } from "components/Screens/Marketplace/Customer/utils/availability";
import { DEFAULT_FROM, DEFAULT_TO, fmtCallDay, fmtMinutes, fmtWindow } from "plg/meeting/meetingOptions";
import PlgIcon from "plg/shell/PlgIcon";
import "plg/meeting/Meeting.scss";

/** The talent the graphics show: the home page's own preview people, so it is the real network with our avatars. */
export type TourPeople = TalentListItem[];

/**
 * A graphic panel above a tour step (prototype tourScale): the product's own pieces, scaled down to fit the frame and
 * inert, so nothing in it can be clicked, focused or read out. `top` pushes the shorter ones down to the middle.
 */
export function TourFrame({ k, top, green = false, children }: { k: number; top?: number; green?: boolean; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.setAttribute("inert", "");
  }, []);
  return (
    <div
      ref={ref}
      className={`tour-art${green ? " tour-art--green" : ""}`}
      aria-hidden="true"
      style={top ? ({ "--top": `${top}px` } as CSSProperties) : undefined}
    >
      <div className="tour-scale" style={{ "--k": k } as CSSProperties}>
        {children}
      </div>
    </div>
  );
}

/** Real cards with the Shortlist button in a set state, whatever the customer's own shortlist holds. */
function Cards({ people, saved }: { people: TourPeople; saved: boolean }) {
  const shortlist = useShortlist();
  return (
    <ShortlistPreviewProvider value={{ ...shortlist, isFull: false, isShortlisted: () => saved }}>
      <div className="tour-list">
        {people.map((t) => (
          // eslint-disable-next-line no-underscore-dangle
          <TalentCard key={t._id} talent={t} shortlistMode />
        ))}
      </div>
    </ShortlistPreviewProvider>
  );
}

// The first call day the meeting panel offers (tomorrow, a weekday) and its default window
const callDay = () => fmtCallDay(nextWeekdays(1)[0]);

/** Step 1: two profiles as Browse shows them. */
export function BrowseArt({ people }: { people: TourPeople }) {
  return (
    <TourFrame k={0.56}>
      <Cards people={people.slice(0, 2)} saved={false} />
    </TourFrame>
  );
}

/** Step 2: one profile shortlisted, and the header's count. */
export function ShortlistArt({ people }: { people: TourPeople }) {
  return (
    <TourFrame k={0.66} top={22}>
      <Cards people={people.slice(2, 3).length ? people.slice(2, 3) : people.slice(0, 1)} saved />
      <div className="tour-count">
        <span className="plg-hc">
          <span>
            <b>1</b> shortlisted
          </span>
        </span>
      </div>
    </TourFrame>
  );
}

/** Step 3: when they are free for a call, as the Request a meeting panel asks it. */
export function RequestArt() {
  const select = (label: string, value: string) => (
    <select className="plg-mtg-select" aria-label={label} value={value} disabled>
      <option>{value}</option>
    </select>
  );
  return (
    <TourFrame k={0.8} top={54}>
      <div className="tour-sheet plg-mtg">
        <h3>When are you free for a call?</h3>
        <div className="plg-mtg-slot">
          <span className="plg-mtg-slot__part">I&apos;m available on {select("Day", callDay())}</span>
          <span className="plg-mtg-slot__part">from {select("From", fmtMinutes(DEFAULT_FROM))}</span>
          <span className="plg-mtg-slot__part">to {select("To", fmtMinutes(DEFAULT_TO))}</span>
        </div>
        <div className="plg-mtg-hint">Times are in your local time zone.</div>
      </div>
    </TourFrame>
  );
}

/** Step 4: the call booked with a hiring expert, the shortlist alongside (not a product component yet). */
export function MeetArt({ people }: { people: TourPeople }) {
  const stack = people.slice(0, 3);
  return (
    <TourFrame k={0.9} top={46} green>
      <div className="tour-meet">
        <div className="plg-ic-tile tour-meet__ic">
          <PlgIcon name="calTick" />
        </div>
        <div className="tour-meet__body">
          <div className="tour-meet__tag">Scheduled</div>
          <div className="tour-meet__name">Meeting with a hiring expert</div>
          <div className="tour-meet__facts">
            <span>
              <PlgIcon name="calDays" size={16} />
              {callDay()}
            </span>
            <span>
              <PlgIcon name="clock" size={16} />
              {fmtWindow(DEFAULT_FROM, DEFAULT_TO)}
            </span>
          </div>
          {stack.length > 0 && (
            <div className="tour-stack">
              {stack.map((t) => (
                // eslint-disable-next-line no-underscore-dangle
                <ProtoAvatar key={t._id} talent={t} size={40} />
              ))}
              <span>
                Your shortlist, {stack.length} {stack.length === 1 ? "profile" : "profiles"}
              </span>
            </div>
          )}
        </div>
      </div>
    </TourFrame>
  );
}
